import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Copy, Archive, Send, FileText, Link2, Eye } from 'lucide-react';

const STATUS_COLORS = { DRAFT: 'secondary', PUBLISHED: 'default', ARCHIVED: 'outline' };

export default function FormTemplateDetailPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/forms/templates/${templateId}`).then(({ data }) => {
      setTemplate(data.data);
      setLoading(false);
    }).catch(() => { toast.error('Failed to load template'); setLoading(false); });
  }, [templateId]);

  const handlePublish = async () => {
    try {
      await api.post(`/forms/templates/${templateId}/publish`);
      toast.success('Template published');
      setTemplate(prev => ({ ...prev, status: 'PUBLISHED' }));
    } catch { toast.error('Failed to publish'); }
  };

  const handleDuplicate = async () => {
    try {
      const { data } = await api.post(`/forms/templates/${templateId}/duplicate`);
      toast.success('Template duplicated');
      navigate(`/forms/templates/${data.data.id}/edit`);
    } catch { toast.error('Failed to duplicate'); }
  };

  const handleArchive = async () => {
    try {
      await api.post(`/forms/templates/${templateId}/archive`);
      toast.success('Template archived');
      setTemplate(prev => ({ ...prev, status: 'ARCHIVED' }));
    } catch { toast.error('Failed to archive'); }
  };

  const handleGenerateLink = async () => {
    try {
      const { data } = await api.post(`/forms/templates/${templateId}/generate-public-link`);
      navigator.clipboard.writeText(data.data.url);
      toast.success('Public link copied to clipboard');
    } catch { toast.error('Failed to generate link'); }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;
  if (!template) return <div className="text-center py-12 text-muted-foreground">Template not found</div>;

  const fields = template.version?.schema_json || [];

  return (
    <div className="space-y-6" data-testid="form-template-detail">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/forms/templates')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">{template.name}</h1>
              <Badge variant={STATUS_COLORS[template.status] || 'secondary'}>{template.status}</Badge>
            </div>
            <p className="text-muted-foreground text-sm">{template.form_code} · {template.module_type}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {template.status === 'PUBLISHED' && (
            <Button variant="outline" size="sm" onClick={handleGenerateLink} data-testid="generate-link-btn">
              <Link2 className="w-4 h-4 mr-2" />Public Link
            </Button>
          )}
          {template.status === 'DRAFT' && (
            <Button variant="outline" size="sm" onClick={handlePublish} data-testid="publish-btn">
              <Send className="w-4 h-4 mr-2" />Publish
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleDuplicate} data-testid="duplicate-btn">
            <Copy className="w-4 h-4 mr-2" />Duplicate
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/forms/templates/${templateId}/edit`)} data-testid="edit-btn">
            <Edit className="w-4 h-4 mr-2" />Edit
          </Button>
          {template.status !== 'ARCHIVED' && (
            <Button variant="outline" size="sm" className="text-red-600" onClick={handleArchive} data-testid="archive-btn">
              <Archive className="w-4 h-4 mr-2" />Archive
            </Button>
          )}
        </div>
      </div>

      {template.description && (
        <Card><CardContent className="py-3"><p className="text-sm text-muted-foreground">{template.description}</p></CardContent></Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Eye className="w-4 h-4" />Form Fields Preview ({fields.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {fields.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No fields defined yet. Click Edit to add fields.</p>
          ) : (
            <div className="space-y-3">
              {fields.map((f, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-dashed last:border-0">
                  <span className="text-xs text-muted-foreground font-mono w-6 text-right shrink-0 pt-0.5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{f.label || f.key || `Field ${i + 1}`}</span>
                      <Badge variant="outline" className="text-xs">{f.type}</Badge>
                      {f.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                    </div>
                    {f.help_text && <p className="text-xs text-muted-foreground mt-0.5">{f.help_text}</p>}
                    {f.options?.length > 0 && <p className="text-xs text-muted-foreground mt-0.5">Options: {f.options.join(', ')}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
